import { useEffect, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import WidgetPlaceholder from "./WidgetPlaceholder";
import "./DonutPieWidget.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

type DonutRow = {
  name: string;
  value: number;
  percentage?: number;
};

type DonutMetadata = {
  total_pa_cases?: number;
};

type Props = {
  isLoading: boolean;
  data: unknown;
};

// ─── Colors ───────────────────────────────────────────────────────────────────

// Outcome colors are semantic: approvals live on the green ramp (first-pass
// darker than appeal), "pending" is a deliberate recessive slate (a state, not
// an identity hue), denied is the shared denial red. Slices carry 2px white
// gaps and a labeled legend, so color is never the only encoding.
const OUTCOME_COLORS: Array<{ match: RegExp; color: string }> = [
  { match: /appeal/i, color: "#10B981" },
  { match: /approv/i, color: "#15803D" },
  { match: /pending/i, color: "#64748B" },
  { match: /denie|denial/i, color: "#DC2626" },
];

// Fallback pool for categories the semantic map doesn't know (fixed order).
const FALLBACK_COLORS = ["#2563EB", "#D97706", "#7C3AED", "#0D9488"];

const colorForOutcome = (name: string, fallbackIndex: number): string => {
  const hit = OUTCOME_COLORS.find((c) => c.match.test(name));
  if (hit) return hit.color;
  return FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length] ?? "#64748B";
};

// ─── Data unwrap ──────────────────────────────────────────────────────────────

// Accepts either the full API response ({ data: rows, metadata }) or the bare
// rows array (WidgetRenderer strips the envelope before passing data down).
const unwrap = (
  raw: unknown,
): { rows: DonutRow[]; metadata: DonutMetadata } => {
  if (Array.isArray(raw)) return { rows: raw as DonutRow[], metadata: {} };
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.data)) {
      return {
        rows: obj.data as DonutRow[],
        metadata: (obj.metadata ?? {}) as DonutMetadata,
      };
    }
  }
  return { rows: [], metadata: {} };
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function DonutPieWidget({ isLoading, data }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const { rows, metadata } = useMemo(() => unwrap(data), [data]);

  const total = useMemo(
    () =>
      metadata.total_pa_cases ??
      rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0),
    [rows, metadata],
  );

  const slices = useMemo(
    () =>
      rows.map((r, i) => {
        const value = Number(r.value) || 0;
        const pct =
          r.percentage ?? (total > 0 ? (value / total) * 100 : 0);
        return {
          name: r.name,
          y: value,
          color: colorForOutcome(r.name, i),
          custom: { pct },
        };
      }),
    [rows, total],
  );

  // Keep the chart sized to its flex container.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let raf = 0;
    const reflow = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() =>
        (chartRef.current as any)?.chart?.reflow(),
      );
    };
    const timer = setTimeout(reflow, 0);
    const ro = new ResizeObserver(reflow);
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        animation: false,
        backgroundColor: "transparent",
        margin: [4, 4, 4, 4],
        style: { fontFamily: "inherit" },
      },
      title: { text: "" },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      accessibility: { enabled: false },
      tooltip: {
        useHTML: true,
        formatter(this: any) {
          const pct = this.point?.options?.custom?.pct ?? 0;
          return `<div style="padding:2px 4px;">
            <div style="font-size:12px;font-weight:700;margin-bottom:2px;">${this.point.name}</div>
            <div style="font-size:11px;">Cases: <b>${Number(this.y).toLocaleString()}</b></div>
            <div style="font-size:11px;">Share: <b>${pct.toFixed(1)}%</b></div>
          </div>`;
        },
      },
      plotOptions: {
        pie: {
          innerSize: "68%",
          size: "92%",
          borderWidth: 2,
          borderColor: "#FFFFFF",
          animation: false,
          dataLabels: { enabled: false },
          states: {
            hover: { halo: { size: 6 }, brightness: 0.05 },
            inactive: { opacity: 0.4 },
          },
        },
      },
      series: [
        {
          type: "pie",
          name: "Outcomes",
          data: slices,
        },
      ],
    }),
    [slices],
  );

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={240} />;
  }

  if (rows.length === 0) {
    return (
      <div
        className="d-flex align-items-center justify-content-center h-100"
        style={{ fontSize: 12.5, color: "var(--bs-secondary-color)" }}
      >
        No outcome data available.
      </div>
    );
  }

  return (
    <div className="donut-widget">
      <div className="donut-widget__chart" ref={wrapperRef}>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { width: "100%", height: "100%" } }}
        />
        <div className="donut-widget__center">
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.1,
              color: "var(--bs-body-color)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {total.toLocaleString()}
          </span>
          <span style={{ fontSize: 10.5, color: "var(--bs-secondary-color)" }}>
            {metadata.total_pa_cases !== undefined ? "PA cases" : "total"}
          </span>
        </div>
      </div>

      <div className="donut-widget__legend">
        {slices.map((s, i) => (
          <div
            key={s.name}
            className="donut-widget__legend-row"
            style={{ animationDelay: `${i * 30}ms` }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: s.color,
                flexShrink: 0,
              }}
            />
            <span
              className="text-truncate"
              style={{ flex: 1, fontSize: 13, color: "var(--bs-body-color)" }}
              title={s.name}
            >
              {s.name}
            </span>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--bs-secondary-color)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {Number(s.y).toLocaleString()}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                minWidth: 48,
                textAlign: "right",
                color: "var(--bs-body-color)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {s.custom.pct.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
